import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Mail, Save, Loader2, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { toast } from 'sonner';

const authHeader = () => {
  const token = localStorage.getItem('dermascan_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/system`;

/**
 * Panneau de configuration des destinataires des notifications email.
 * S'affiche en tête de l'onglet Candidatures et permet à l'admin de choisir
 * précisément quelles adresses (@phciesada.org ou autres) reçoivent :
 *   - Les notifications de candidature (career)
 *   - Les rappels SADA Academy (academy)
 */
const NotificationEmailsPanel = () => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [careersText, setCareersText] = useState('');
  const [academyText, setAcademyText] = useState('');
  const [careersList, setCareersList] = useState([]);
  const [academyList, setAcademyList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BASE}/notification-emails`, { headers: authHeader() })
      .then((res) => {
        if (cancelled) return;
        setCareersList(res.data.careers_emails || []);
        setAcademyList(res.data.academy_emails || []);
        setCareersText((res.data.careers_emails || []).join(', '));
        setAcademyText((res.data.academy_emails || []).join(', '));
      })
      .catch(() => {
        // silent — l'admin verra les valeurs par défaut (vides)
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${BASE}/notification-emails`,
        { careers_emails: careersText, academy_emails: academyText },
        { headers: authHeader() },
      );
      setCareersList(res.data.careers_emails || []);
      setAcademyList(res.data.academy_emails || []);
      setCareersText((res.data.careers_emails || []).join(', '));
      setAcademyText((res.data.academy_emails || []).join(', '));
      toast.success('Destinataires mis à jour');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const summary =
    careersList.length === 0 && academyList.length === 0
      ? 'Non configurée — les admins de la base reçoivent tout par défaut'
      : `Candidatures → ${careersList.length || 'admins DB'} · Academy → ${academyList.length || 'admins DB'}`;

  return (
    <Card className="border-[#C9A086]/40 bg-gradient-to-r from-white to-[#FAF6F1]">
      <CardContent className="p-4">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between text-left"
          data-testid="notif-emails-toggle"
        >
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#5D4E37]" />
            <div>
              <div className="font-semibold text-[#5D4E37]">
                Adresses de notification
              </div>
              <div className="text-xs text-slate-500">{summary}</div>
            </div>
          </div>
          {open ? (
            <ChevronUp className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          )}
        </button>

        {open && (
          <div className="mt-4 space-y-4 border-t pt-4" data-testid="notif-emails-panel">
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-blue-50 border border-blue-200 rounded p-2">
              <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>
                Choisissez précisément vers quelles adresses partent les emails.
                Séparez plusieurs adresses par des virgules. Si vous laissez vide,
                les emails partiront vers les comptes admin de la base (comportement par défaut).
              </span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-4 text-slate-400">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
              </div>
            ) : (
              <>
                <label className="block">
                  <span className="text-sm font-medium text-[#5D4E37]">
                    📨 Candidatures reçues (nouvelles postulations)
                  </span>
                  <input
                    type="text"
                    value={careersText}
                    onChange={(e) => setCareersText(e.target.value)}
                    placeholder="ex: recrutement@phciesada.org, contact@phciesada.org"
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                    data-testid="notif-careers-input"
                  />
                  {careersList.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {careersList.map((e) => (
                        <Badge key={e} variant="outline" className="text-[10px]">
                          {e}
                        </Badge>
                      ))}
                    </div>
                  )}
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-[#5D4E37]">
                    🎓 SADA Academy (rappels J-30 / J-7 / J+1 en CC)
                  </span>
                  <input
                    type="text"
                    value={academyText}
                    onChange={(e) => setAcademyText(e.target.value)}
                    placeholder="ex: rh@phciesada.org, direction@phciesada.org"
                    className="mt-1 w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5D4E37]"
                    data-testid="notif-academy-input"
                  />
                  {academyList.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {academyList.map((e) => (
                        <Badge key={e} variant="outline" className="text-[10px]">
                          {e}
                        </Badge>
                      ))}
                    </div>
                  )}
                </label>

                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={save}
                    disabled={saving}
                    className="bg-[#5D4E37] hover:bg-[#4A3E2C] text-white"
                    data-testid="notif-emails-save"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationEmailsPanel;
